import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface FeeReportsProps {
  domain: string;
}

interface FeeRecord {
  id: string;
  studentId: string;
  studentName: string;
  className: string;
  section: string;
  feeType: string;
  amount: number;
  paidAmount: number;
  dueDate: string;
  paidDate?: string;
  status: 'paid' | 'pending' | 'overdue' | 'partial';
  paymentMode?: string;
  receiptNo?: string;
}

interface ClassSummary {
  className: string;
  totalStudents: number;
  totalAmount: number;
  collectedAmount: number;
  pendingAmount: number;
  collectionRate: number;
}

const FeeReports: React.FC<FeeReportsProps> = ({ domain }) => {
  const [feeRecords, setFeeRecords] = useState<FeeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedFeeType, setSelectedFeeType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'summary' | 'classwise' | 'detailed'>('summary');
  const [searchTerm, setSearchTerm] = useState('');

  const feeTypes = [
    { id: 'tuition', name: 'Tuition Fee', color: '#5C2597' },
    { id: 'transport', name: 'Transport Fee', color: '#E83E8C' },
    { id: 'lab', name: 'Lab Fee', color: '#17A2B8' },
    { id: 'library', name: 'Library Fee', color: '#6F42C1' },
    { id: 'exam', name: 'Exam Fee', color: '#FFC107' },
    { id: 'sports', name: 'Sports Fee', color: '#28A745' },
    { id: 'admission', name: 'Admission Fee', color: '#FD7E14' },
    { id: 'other', name: 'Other Fees', color: '#6C757D' },
  ];

  useEffect(() => {
    fetchFeeData();
  }, [domain]);

  const fetchFeeData = async () => {
    if (!domain) return;
    setLoading(true);

    try {
      // Fetch fees collection
      const feesRef = collection(db, `school-users/${domain}/fees`);
      const snapshot = await getDocs(feesRef);

      const records: FeeRecord[] = [];
      snapshot.forEach(doc => {
        records.push({ id: doc.id, ...doc.data() } as FeeRecord);
      });

      if (records.length === 0) {
        // Generate sample data for demonstration
        setFeeRecords(generateSampleFeeData());
      } else {
        setFeeRecords(records);
      }
    } catch (error) {
      console.error('Error fetching fee data:', error);
      setFeeRecords(generateSampleFeeData());
    } finally {
      setLoading(false);
    }
  };

  const generateSampleFeeData = (): FeeRecord[] => {
    const classes = ['Class 1A', 'Class 1B', 'Class 2A', 'Class 2B', 'Class 3A', 'Class 3B',
                     'Class 4A', 'Class 5A', 'Class 6A', 'Class 7A', 'Class 8A', 'Class 9A', 'Class 10A'];
    const records: FeeRecord[] = [];
    const names = ['Aarav', 'Vivaan', 'Aditya', 'Vihaan', 'Arjun', 'Sai', 'Reyansh', 'Ayaan',
                   'Ananya', 'Isha', 'Saanvi', 'Aanya', 'Pari', 'Myra', 'Diya', 'Kiara'];
    const surnames = ['Sharma', 'Patel', 'Singh', 'Kumar', 'Reddy', 'Gupta', 'Verma', 'Joshi'];

    classes.forEach((cls) => {
      const studentsInClass = Math.floor(Math.random() * 15) + 20; // 20-35 students per class

      for (let i = 0; i < studentsInClass; i++) {
        const firstName = names[Math.floor(Math.random() * names.length)];
        const lastName = surnames[Math.floor(Math.random() * surnames.length)];
        const studentName = `${firstName} ${lastName}`;
        const studentId = `STU${cls.replace(/\s/g, '')}${String(i + 1).padStart(3, '0')}`;

        // Generate fee records for different fee types
        feeTypes.slice(0, 4).forEach(feeType => {
          const baseAmount = feeType.id === 'tuition' ? 15000 :
                            feeType.id === 'transport' ? 5000 :
                            feeType.id === 'lab' ? 2000 : 1000;

          const random = Math.random();
          let status: 'paid' | 'pending' | 'overdue' | 'partial';
          let paidAmount: number;

          if (random > 0.7) {
            status = 'paid';
            paidAmount = baseAmount;
          } else if (random > 0.5) {
            status = 'partial';
            paidAmount = Math.floor(baseAmount * (0.3 + Math.random() * 0.5));
          } else if (random > 0.3) {
            status = 'pending';
            paidAmount = 0;
          } else {
            status = 'overdue';
            paidAmount = 0;
          }

          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() - Math.floor(Math.random() * 60) + 30);

          records.push({
            id: `${studentId}_${feeType.id}`,
            studentId,
            studentName,
            className: cls,
            section: cls.slice(-1),
            feeType: feeType.id,
            amount: baseAmount,
            paidAmount,
            dueDate: dueDate.toISOString().split('T')[0],
            paidDate: status === 'paid' || status === 'partial' ?
                      new Date(dueDate.getTime() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : undefined,
            status,
            paymentMode: status !== 'pending' && status !== 'overdue' ?
                        ['Cash', 'Online', 'Cheque', 'Bank Transfer'][Math.floor(Math.random() * 4)] : undefined,
            receiptNo: status === 'paid' ? `RCP${Math.floor(Math.random() * 100000)}` : undefined,
          });
        });
      }
    });

    return records;
  };

  const uniqueClasses = [...new Set(feeRecords.map(r => r.className))].sort();

  const filteredRecords = feeRecords.filter(record => {
    const matchesClass = selectedClass === 'all' || record.className === selectedClass;
    const matchesStatus = selectedStatus === 'all' || record.status === selectedStatus;
    const matchesFeeType = selectedFeeType === 'all' || record.feeType === selectedFeeType;
    const matchesSearch = searchTerm === '' ||
                         record.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.studentId.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesClass && matchesStatus && matchesFeeType && matchesSearch;
  });

  // Calculate summaries
  const totalAmount = filteredRecords.reduce((sum, r) => sum + r.amount, 0);
  const collectedAmount = filteredRecords.reduce((sum, r) => sum + r.paidAmount, 0);
  const pendingAmount = totalAmount - collectedAmount;
  const collectionRate = totalAmount > 0 ? Math.round((collectedAmount / totalAmount) * 100) : 0;

  const paidCount = filteredRecords.filter(r => r.status === 'paid').length;
  const pendingCount = filteredRecords.filter(r => r.status === 'pending').length;
  const overdueCount = filteredRecords.filter(r => r.status === 'overdue').length;
  const partialCount = filteredRecords.filter(r => r.status === 'partial').length;

  // Class-wise summary
  const classSummary: ClassSummary[] = uniqueClasses.map(cls => {
    const classRecords = filteredRecords.filter(r => r.className === cls);
    const uniqueStudents = new Set(classRecords.map(r => r.studentId)).size;
    const total = classRecords.reduce((sum, r) => sum + r.amount, 0);
    const collected = classRecords.reduce((sum, r) => sum + r.paidAmount, 0);
    return {
      className: cls,
      totalStudents: uniqueStudents,
      totalAmount: total,
      collectedAmount: collected,
      pendingAmount: total - collected,
      collectionRate: total > 0 ? Math.round((collected / total) * 100) : 0,
    };
  });

  const formatCurrency = (amount: number): string => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)} K`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'danger';
      case 'partial': return 'info';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100 bg-primary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="small opacity-75">Total Fees</div>
                  <div className="fs-4 fw-bold">{formatCurrency(totalAmount)}</div>
                </div>
                <i className="bi bi-currency-rupee fs-2 opacity-50"></i>
              </div>
              <div className="progress mt-3" style={{ height: 4 }}>
                <div className="progress-bar bg-white" style={{ width: `${collectionRate}%` }}></div>
              </div>
              <div className="small mt-1 opacity-75">{collectionRate}% collected</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-muted small">Collected</div>
                  <div className="fs-4 fw-bold text-success">{formatCurrency(collectedAmount)}</div>
                </div>
                <div className="rounded-circle bg-success bg-opacity-10 p-3">
                  <i className="bi bi-check-circle-fill text-success fs-4"></i>
                </div>
              </div>
              <div className="small text-muted mt-2">{paidCount} fully paid</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-muted small">Pending</div>
                  <div className="fs-4 fw-bold text-warning">{formatCurrency(pendingAmount)}</div>
                </div>
                <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                  <i className="bi bi-clock-fill text-warning fs-4"></i>
                </div>
              </div>
              <div className="small text-muted mt-2">{pendingCount + partialCount} pending</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-muted small">Overdue</div>
                  <div className="fs-4 fw-bold text-danger">{overdueCount}</div>
                </div>
                <div className="rounded-circle bg-danger bg-opacity-10 p-3">
                  <i className="bi bi-exclamation-triangle-fill text-danger fs-4"></i>
                </div>
              </div>
              <div className="small text-muted mt-2">Require follow-up</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label small text-muted">Class</label>
              <select
                className="form-select"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <option value="all">All Classes</option>
                {uniqueClasses.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted">Status</label>
              <select
                className="form-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="partial">Partial</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted">Fee Type</label>
              <select
                className="form-select"
                value={selectedFeeType}
                onChange={(e) => setSelectedFeeType(e.target.value)}
              >
                <option value="all">All Types</option>
                {feeTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted">Search Student</label>
              <input
                type="text"
                className="form-control"
                placeholder="Name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <div className="btn-group w-100">
                <button
                  className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('summary')}
                >
                  Summary
                </button>
                <button
                  className={`btn ${viewMode === 'classwise' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('classwise')}
                >
                  Class-wise
                </button>
                <button
                  className={`btn ${viewMode === 'detailed' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('detailed')}
                >
                  Detailed
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'summary' ? (
        /* Fee Type Summary */
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-bar-chart me-2 text-primary"></i>
                  Fee Type Breakdown
                </h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {feeTypes.map(type => {
                    const typeRecords = filteredRecords.filter(r => r.feeType === type.id);
                    const typeTotal = typeRecords.reduce((sum, r) => sum + r.amount, 0);
                    const typeCollected = typeRecords.reduce((sum, r) => sum + r.paidAmount, 0);
                    const typeRate = typeTotal > 0 ? Math.round((typeCollected / typeTotal) * 100) : 0;

                    if (typeTotal === 0) return null;

                    return (
                      <div key={type.id} className="col-md-6">
                        <div className="p-3 bg-light rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-semibold">{type.name}</span>
                            <span className="badge" style={{ backgroundColor: type.color }}>
                              {typeRate}%
                            </span>
                          </div>
                          <div className="progress mb-2" style={{ height: 8 }}>
                            <div
                              className="progress-bar"
                              style={{ width: `${typeRate}%`, backgroundColor: type.color }}
                            ></div>
                          </div>
                          <div className="d-flex justify-content-between small text-muted">
                            <span>Collected: {formatCurrency(typeCollected)}</span>
                            <span>Total: {formatCurrency(typeTotal)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-pie-chart me-2 text-info"></i>
                  Status Distribution
                </h6>
              </div>
              <div className="card-body">
                {[
                  { status: 'paid', label: 'Paid', count: paidCount, color: '#28a745' },
                  { status: 'partial', label: 'Partial', count: partialCount, color: '#17a2b8' },
                  { status: 'pending', label: 'Pending', count: pendingCount, color: '#ffc107' },
                  { status: 'overdue', label: 'Overdue', count: overdueCount, color: '#dc3545' },
                ].map(item => {
                  const percentage = filteredRecords.length > 0
                    ? Math.round((item.count / filteredRecords.length) * 100)
                    : 0;
                  return (
                    <div key={item.status} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="small">{item.label}</span>
                        <span className="small fw-bold">{item.count} ({percentage}%)</span>
                      </div>
                      <div className="progress" style={{ height: 6 }}>
                        <div
                          className="progress-bar"
                          style={{ width: `${percentage}%`, backgroundColor: item.color }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === 'classwise' ? (
        /* Class-wise Summary */
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Class</th>
                  <th className="text-center">Students</th>
                  <th className="text-end">Total Amount</th>
                  <th className="text-end">Collected</th>
                  <th className="text-end">Pending</th>
                  <th>Collection Rate</th>
                </tr>
              </thead>
              <tbody>
                {classSummary.map(cls => (
                  <tr key={cls.className}>
                    <td className="fw-semibold">{cls.className}</td>
                    <td className="text-center">{cls.totalStudents}</td>
                    <td className="text-end">{formatCurrency(cls.totalAmount)}</td>
                    <td className="text-end text-success">{formatCurrency(cls.collectedAmount)}</td>
                    <td className="text-end text-warning">{formatCurrency(cls.pendingAmount)}</td>
                    <td style={{ width: 200 }}>
                      <div className="d-flex align-items-center">
                        <div className="progress flex-grow-1 me-2" style={{ height: 8 }}>
                          <div
                            className={`progress-bar ${cls.collectionRate >= 80 ? 'bg-success' : cls.collectionRate >= 50 ? 'bg-warning' : 'bg-danger'}`}
                            style={{ width: `${cls.collectionRate}%` }}
                          ></div>
                        </div>
                        <small className="fw-bold">{cls.collectionRate}%</small>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr className="fw-bold">
                  <td>Total</td>
                  <td className="text-center">{new Set(filteredRecords.map(r => r.studentId)).size}</td>
                  <td className="text-end">{formatCurrency(totalAmount)}</td>
                  <td className="text-end text-success">{formatCurrency(collectedAmount)}</td>
                  <td className="text-end text-warning">{formatCurrency(pendingAmount)}</td>
                  <td>{collectionRate}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        /* Detailed View */
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold">
              <i className="bi bi-list-ul me-2"></i>
              Detailed Fee Records ({filteredRecords.length})
            </h6>
            <button className="btn btn-sm btn-outline-success">
              <i className="bi bi-download me-2"></i>Export to Excel
            </button>
          </div>
          <div className="table-responsive" style={{ maxHeight: '500px' }}>
            <table className="table table-hover table-sm mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Class</th>
                  <th>Fee Type</th>
                  <th className="text-end">Amount</th>
                  <th className="text-end">Paid</th>
                  <th>Due Date</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords
                  .sort((a, b) => {
                    if (a.status === 'overdue' && b.status !== 'overdue') return -1;
                    if (b.status === 'overdue' && a.status !== 'overdue') return 1;
                    return a.className.localeCompare(b.className);
                  })
                  .slice(0, 100)
                  .map(record => {
                    const feeTypeInfo = feeTypes.find(t => t.id === record.feeType);
                    return (
                      <tr key={record.id}>
                        <td><code>{record.studentId}</code></td>
                        <td className="fw-semibold">{record.studentName}</td>
                        <td>{record.className}</td>
                        <td>
                          <span style={{ color: feeTypeInfo?.color }}>{feeTypeInfo?.name}</span>
                        </td>
                        <td className="text-end">{formatCurrency(record.amount)}</td>
                        <td className="text-end">{formatCurrency(record.paidAmount)}</td>
                        <td>{record.dueDate}</td>
                        <td className="text-center">
                          <span className={`badge bg-${getStatusColor(record.status)}`}>
                            {record.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {filteredRecords.length > 100 && (
            <div className="card-footer bg-white text-center text-muted small">
              Showing first 100 records of {filteredRecords.length} total
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeeReports;
